import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CardContainer from "./CardContainer";
import ClickableText from "../Texts/ClickableText";
import { COPY_VALUES_IN_OTHER_ADGROUPS } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useFormContext } from "react-hook-form";
import { getReactHookFormNestedError } from "../../utils/errors";

/**
 * Reusable CardContainer component with optional copy functionality and Grid wrapper
 * @param {Object} props
 * @param {string|ReactNode} props.title - The main title of the card
 * @param {boolean} props.disabled - Whether the copy button should be disabled
 * @param {boolean} props.hasCopyValue - Whether to show the copy button
 * @param {Function} props.onCopyCallback - Callback function when copy is clicked
 * @param {string} props.name - Field name to construct the id (e.g., "longHeadlines")
 * @param {string} props.basicPath - Base path for the id (e.g., "assetsGroups.[0]")
 * @param {string} props.copyText - Translation key for copy text (default: COPY_VALUES_IN_OTHER_ADGROUPS)
 * @param {Object} props.gridProps - Props to pass to the Grid item (e.g., { xs: 12, sm: 6 })
 * @param {ReactNode} props.children - Content to render inside the CardContainer
 */
const CardContainerWithCopy = ({
  title,
  disabled = false,
  hasCopyValue = false,
  onCopyCallback,
  id,
  copyText = COPY_VALUES_IN_OTHER_ADGROUPS,
  children,
}) => {
  const { t } = useTranslationApp();
  const {
    formState: { errors },
  } = useFormContext();

  const error = getReactHookFormNestedError(errors, id);

  const titleContent = (
    <Box sx={{ display: "flex", flexDirection: "column" }} id={id}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {error?.message && (
        <Typography variant="caption" color="error">
          {error?.message}
        </Typography>
      )}
      {hasCopyValue && (
        <ClickableText disabled={disabled} onClick={onCopyCallback}>
          {t(copyText)}
        </ClickableText>
      )}
    </Box>
  );

  const cardContent = (
    <CardContainer title={titleContent} dynamicHeight={true}>
      {children}
    </CardContainer>
  );

  // Otherwise, return just the CardContainer
  return cardContent;
};

export default CardContainerWithCopy;
